import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUpload from '../../components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPartner = { name: '', logo: '', website: '', order: 0, active: true };

export default function AdminPartners() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyPartner });
  const token = localStorage.getItem('itcs-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => axios.get(`${API}/admin/partners`, { headers }).then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...emptyPartner }); setEditing(null); setDialogOpen(true); };
  const openEdit = (item) => { setForm({ ...item }); setEditing(item.id); setDialogOpen(true); };
  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist erforderlich'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/admin/partners/${editing}`, form, { headers });
        toast.success('Partner aktualisiert');
      } else {
        await axios.post(`${API}/admin/partners`, form, { headers });
        toast.success('Partner erstellt');
      }
      setDialogOpen(false); load();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Partner wirklich löschen?')) return;
    try { await axios.delete(`${API}/admin/partners/${id}`, { headers }); toast.success('Gelöscht'); load(); }
    catch { toast.error('Fehler'); }
  };

  return (
    <div data-testid="admin-partners">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Partner verwalten</h1>
          <p className="text-sm text-slate-500 mt-1">Partnerlogos für die Startseite</p>
        </div>
        <Button onClick={openNew} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="add-partner-btn"><Plus className="w-4 h-4 mr-2" /> Neuer Partner</Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Logo</TableHead><TableHead>Name</TableHead><TableHead>Website</TableHead><TableHead>Reihenfolge</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aktionen</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-12">Noch keine Partner angelegt. Klicken Sie auf „Neuer Partner".</TableCell></TableRow>
            )}
            {items.map(p => (
              <TableRow key={p.id}>
                <TableCell>
                  {p.logo
                    ? <img src={p.logo} alt={p.name} className="h-10 w-auto max-w-[120px] object-contain" />
                    : <div className="h-10 w-24 bg-slate-100 rounded border border-dashed border-slate-300 flex items-center justify-center text-[10px] text-slate-400">Kein Logo</div>}
                </TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="text-sm text-slate-500 truncate max-w-[200px]">{p.website || '—'}</TableCell>
                <TableCell className="text-sm text-slate-500">{p.order}</TableCell>
                <TableCell>{p.active ? <Badge className="bg-emerald-100 text-emerald-700">Aktiv</Badge> : <Badge variant="outline">Inaktiv</Badge>}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-partner-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="text-red-500" data-testid={`delete-partner-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Partner bearbeiten' : 'Neuer Partner'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => updateForm('name', e.target.value)} placeholder="z.B. Herschenbach" data-testid="partner-name-input" /></div>
            <div><Label className="text-xs">Website (optional)</Label><Input value={form.website} onChange={e => updateForm('website', e.target.value)} placeholder="https://..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Reihenfolge</Label><Input type="number" value={form.order} onChange={e => updateForm('order', parseInt(e.target.value) || 0)} /></div>
              <div className="flex items-center gap-3 pt-5"><Switch checked={form.active} onCheckedChange={v => updateForm('active', v)} /><Label className="text-xs">Aktiv</Label></div>
            </div>
            <ImageUpload value={form.logo} onChange={v => updateForm('logo', v)} label="Logo (PNG mit transparentem Hintergrund empfohlen)" />
            <p className="text-xs text-slate-500">Empfohlen: PNG-Datei mit transparentem Hintergrund, ca. 240×120 px. Wird auf der Startseite proportional dargestellt.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="bg-[#066ce7] text-white" data-testid="save-partner-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
